// Ported from daisyUI v5 — `stack` component (MIT License).
// Upstream: https://github.com/saadeghi/daisyui  (packages/daisyui/src/components/stack.css)
// daisyUI is MIT © Pouya Saadeghi. Attribution retained per the MIT license.
// This is a vanilla-CSS, tokenized re-implementation — no Tailwind classes.

/**
 * @component ry-stack-overlay
 * @category  layout
 * @summary   Z-axis overlay layout — stacks its children on top of each other like a deck of cards. The first child sits on top; each following child peeks out behind it, slightly scaled down and offset, producing a "pile" look. Port of daisyUI's `stack`.
 *
 * Reference: daisyUI v5 `stack`. The pile effect is achieved purely with CSS
 * grid: every child is placed into the SAME 5×5 grid cell but spans a
 * progressively wider/taller area and sits at a lower z-index, so each layer
 * shows a thin edge behind the one in front. There is no JS animation — this is
 * a CSS-only display primitive (`setup()` is intentionally empty, like
 * `ry-stat`'s data-driven render but with nothing to render).
 *
 * @attribute align   'bottom' | 'top' | 'center'   default='bottom'   Which direction the peeking edges fan out. `bottom` (default) offsets following layers DOWN; `top` offsets them UP; `center` keeps the pile vertically centered while still revealing side edges.
 * @attribute depth   number?                                          Optional hint for how many layers reveal an edge. Beyond `depth`, deeper children collapse flush behind the visible pile (no extra peek). Omit to let every child peek (daisyUI default behavior, capped at 3 visible offsets by the grid template).
 *
 * @content   Two or more block-level children — typically `<ry-card>`, `<div>`, or `<img>`. The FIRST child renders on top (highest z-index); subsequent children stack behind it. With a single child there is nothing to pile and it renders normally.
 *
 * @dom
 *   ry-stack-overlay[align?][depth?]    (display: inline-grid)
 *     > {child 1}    grid spans innermost cell, z highest  → on TOP
 *     > {child 2}    grid spans middle cell,  z mid        → peeks behind #1
 *     > {child 3+}   grid spans outer cell,   z lowest     → peeks behind #2
 *   (no internal transform — children are placed as-authored, the grid does the piling)
 *
 * @gotcha  This is NOT `<ry-stack>`. `<ry-stack>` is a vertical flex layout (a column of stacked-in-flow children with a gap). `<ry-stack-overlay>` stacks children on the Z-AXIS — they overlap in one spot like a card pile. Different component, different file, different CSS. Reaching for a vertical list? Use `<ry-stack>`. Reaching for an overlapping pile? Use `<ry-stack-overlay>`.
 *
 * @gotcha  Child ORDER is z-order. The first DOM child is on top, not the last. This is the inverse of the natural painting order you might expect — the grid + explicit z-index put the first child forward so it reads as "the card on top of the deck."
 *
 * @gotcha  Pure CSS, no JS state. There are no events, methods, or `data-ry-state`. Setting/removing `align` or `depth` re-lays out instantly via CSS attribute selectors; there is nothing to call.
 *
 * @gotcha  Children should be visually opaque (have their own background) for the pile to read. Transparent children let the layers behind bleed through and the peek effect disappears. `<ry-card>` works out of the box because it ships a background.
 *
 * @example basic
 * ```html
 * <!-- Give the children a real width — like daisyUI's stack examples — so the
 *      grid doesn't collapse to content. The first card sits on top; the others
 *      peek out as thin edges behind it. -->
 * <ry-stack-overlay style="width: 16rem">
 *   <ry-card style="text-align:center">Top of the pile</ry-card>
 *   <ry-card>Middle</ry-card>
 *   <ry-card>Bottom</ry-card>
 * </ry-stack-overlay>
 * ```
 *
 * @example align-top
 * ```html
 * <ry-stack-overlay align="top" style="width: 16rem">
 *   <ry-card style="text-align:center">Top of the pile</ry-card>
 *   <ry-card>Middle</ry-card>
 *   <ry-card>Bottom</ry-card>
 * </ry-stack-overlay>
 * ```
 *
 * @example align-center
 * ```html
 * <ry-stack-overlay align="center" style="width: 16rem">
 *   <ry-card style="text-align:center">Top of the pile</ry-card>
 *   <ry-card>Middle</ry-card>
 *   <ry-card>Bottom</ry-card>
 * </ry-stack-overlay>
 * ```
 *
 * @example images
 * ```html
 * <ry-stack-overlay style="width: 16rem">
 *   <img src="https://placehold.co/256x160/3b82f6/fff?text=1" alt="" style="width:100%;border-radius:var(--ry-radius-lg);display:block">
 *   <img src="https://placehold.co/256x160/8b5cf6/fff?text=2" alt="" style="width:100%;border-radius:var(--ry-radius-lg);display:block">
 *   <img src="https://placehold.co/256x160/ec4899/fff?text=3" alt="" style="width:100%;border-radius:var(--ry-radius-lg);display:block">
 * </ry-stack-overlay>
 * ```
 *
 * @tutorial children-preserved-in-order
 * ```js
 * // The component does NOT transform or reorder its markup. The first child
 * // stays first (and renders on top via CSS z-index); order is untouched.
 * const el = mount(`
 *   <ry-stack-overlay>
 *     <div id="a">A</div>
 *     <div id="b">B</div>
 *     <div id="c">C</div>
 *   </ry-stack-overlay>
 * `);
 * const ids = [...el.children].map(c => c.id);
 * await expectEqual(ids.join(','), 'a,b,c', 'children kept in authored order');
 * ```
 *
 * @tutorial align-attribute-is-reflected
 * ```js
 * // align is a plain CSS hook — no JS normalizes it; the attribute is read
 * // verbatim by the structure stylesheet.
 * const el = mount('<ry-stack-overlay align="top"><div>x</div><div>y</div></ry-stack-overlay>');
 * await expectAttr(el, 'align', 'top', 'align attribute present and unchanged');
 * el.setAttribute('align', 'center');
 * await expectAttr(el, 'align', 'center', 'align updates live');
 * ```
 *
 * @tutorial no-events-or-state
 * ```js
 * // Wrong assumption: a layout component manages state. ry-stack-overlay is
 * // inert — no data-ry-state, no events. It only piles its children with CSS.
 * const el = mount('<ry-stack-overlay><div>x</div><div>y</div></ry-stack-overlay>');
 * await expectEqual(el.getAttribute('data-ry-state'), null, 'no data-ry-state managed');
 * await expectEqual(el.tagName.toLowerCase(), 'ry-stack-overlay', 'is the overlay, NOT ry-stack');
 * ```
 *
 * @see ry-stack
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyStackOverlay extends RyElement {
  // CSS-only display primitive. No markup transform, no events, no state.
  // setup() exists for symmetry with the RyElement lifecycle but is a no-op,
  // mirroring how a pure layout component (the pile is entirely grid + z-index)
  // needs no JavaScript at runtime.
  setup(): void {}
}

customElements.define('ry-stack-overlay', RyStackOverlay);
